<repository-write-access-modal>
    <!--
      Reusable read-vs-write scope modal + OAuth flow (METALARK-979).

      Explains what write access enables and runs the OAuth popup at the chosen
      capability. Self-contained so any page that needs to request write access
      (the admin repositories page today; the content-migration target pages
      later) can mount one instance and react to `oncomplete`.

      Props:
        isopen      {boolean}  - controls the dialog (parent toggles it)
        mode        {string}   - 'connect' (read-only default + optional write
                                  checkbox) or 'upgrade' (request write on an
                                  already-connected repo)
        repository  {object}   - the integration ({ repositoryId, displayName })
        oncomplete  {fn}       - called with the granted capability after the
                                  OAuth popup closes
        oncancel    {fn}       - called when the user dismisses the modal
        onerror     {fn}       - called with a message if the popup cannot open
    -->
    <dialog id="repository-write-access-modal" class="modal" aria-labelledby="repository-write-access-modal-title">
        <div class="modal-box">
            <h3 id="repository-write-access-modal-title" class="font-bold text-lg mb-2 flex items-center gap-2 text-primary">
                <i class="icon-metalark-link text-primary text-2xl"></i>
                { props.mode === 'connect'
                    ? locale?.t('admin_repositories_connect')
                    : locale?.t('admin_repositories_enablewrite') }
            </h3>

            <div class="space-y-3 py-2">
                <p class="text-sm" if="{props.mode === 'connect'}">
                    {locale?.t('admin_repositories_writeaccess_connect_intro', { name: repositoryName() })}
                </p>
                <p class="text-sm" if="{props.mode !== 'connect'}">
                    {locale?.t('admin_repositories_writeaccess_intro', { name: repositoryName() })}
                </p>

                <div class="bg-base-200 rounded p-3 text-sm">
                    <p>{locale?.t('admin_repositories_writeaccess_what')}</p>
                    <p class="mt-2 opacity-80">{locale?.t('admin_repositories_writeaccess_reassure')}</p>
                </div>

                <label if="{props.mode === 'connect'}" class="label cursor-pointer justify-start gap-2 mt-1">
                    <input type="checkbox" class="checkbox checkbox-sm"
                        id="repository-write-access-grantnow"
                        checked="{state.grantWrite}"
                        onclick="{toggleGrantWrite}"
                        aria-label="{locale?.t('admin_repositories_writeaccess_grantnow')}">
                    <span class="text-sm">{locale?.t('admin_repositories_writeaccess_grantnow')}</span>
                </label>
            </div>

            <div class="modal-action">
                <button class="btn btn-ghost" onclick="{cancel}">{locale?.t('button_cancel')}</button>
                <button class="btn btn-primary" onclick="{proceed}">
                    <i class="icon-metalark-link me-1"></i>
                    { props.mode === 'connect'
                        ? locale?.t('admin_repositories_connect')
                        : locale?.t('admin_repositories_enablewrite') }
                </button>
            </div>
        </div>
        <form method="dialog" class="modal-backdrop">
            <button onclick="{cancel}" aria-label="{locale?.t('button_close')}">{locale?.t('button_close')}</button>
        </form>
    </dialog>

    <script>
        import { getRepositoryService } from '../services/service-loader';

        const MODAL_ID = 'repository-write-access-modal';

        export default {
            state: { grantWrite: false },

            // Never render an empty name in the intro copy: fall back to the
            // provider's display name (always available) when a connection has no
            // displayName yet.
            repositoryName() {
                const repo = this.props.repository || {};
                return repo.displayName
                    || this.tools?.getProviderName?.(repo.provider)
                    || repo.provider
                    || '';
            },

            onMounted() { this.syncOpen(); },
            onUpdated() { this.syncOpen(); },

            // Keep the native <dialog> in sync with the parent-controlled isopen prop.
            syncOpen() {
                const dialog = document.getElementById(MODAL_ID);
                if (!dialog) return;
                if (this.props.isopen && !dialog.open) {
                    this.state.grantWrite = false; // reset the opt-in each time it opens
                    dialog.showModal();
                } else if (!this.props.isopen && dialog.open) {
                    dialog.close();
                }
            },

            toggleGrantWrite(e) {
                this.update({ grantWrite: !!e.target.checked });
            },

            cancel() {
                const dialog = document.getElementById(MODAL_ID);
                if (dialog) dialog.close();
                if (this.props.oncancel) this.props.oncancel();
            },

            async proceed() {
                // Connect mode honors the opt-in checkbox; upgrade mode always
                // requests write.
                const capability = this.props.mode === 'connect'
                    ? (this.state.grantWrite ? 'write' : 'read')
                    : 'write';
                const dialog = document.getElementById(MODAL_ID);
                if (dialog) dialog.close();
                await this.startOAuth(capability);
            },

            // Open the provider consent popup at the chosen capability and notify
            // the parent once it closes (the code exchange happens in the popup).
            async startOAuth(capability) {
                try {
                    const repositoryService = await getRepositoryService();
                    const response = await repositoryService.getOAuthUrl(this.props.repository.repositoryId, capability);

                    sessionStorage.setItem('oauth_repository_id', this.props.repository.repositoryId);
                    sessionStorage.setItem('oauth_integration_name', this.props.repository.displayName);
                    sessionStorage.setItem('oauth_state', response.state);

                    const width = 600;
                    const height = 700;
                    const left = window.screenX + (window.outerWidth - width) / 2;
                    const top = window.screenY + (window.outerHeight - height) / 2;
                    const popup = window.open(
                        response.authorizationUrl,
                        'oauth_popup',
                        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
                    );

                    if (!popup) {
                        if (this.props.onerror) this.props.onerror(this.locale?.t('admin_repositories_alert_popupblocked'));
                        return;
                    }
                    popup.focus();

                    const pollTimer = setInterval(() => {
                        if (popup.closed) {
                            clearInterval(pollTimer);
                            if (this.props.oncomplete) this.props.oncomplete(capability);
                        }
                    }, 500);
                } catch (error) {
                    if (this.props.onerror) {
                        this.props.onerror(error.response?.data?.error?.message || error.message);
                    }
                }
            }
        };
    </script>
</repository-write-access-modal>
